import { useState, useEffect } from 'react';
import { FaArrowLeft, FaEnvelope, FaWhatsapp, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import appConfig from '../../../config/app-config';

interface Message {
    messageId: string;
    recipient: string;
    recipientType: string;
    status: string;
    sentAt?: string;
    deliveredAt?: string;
}

interface MessageListProps {
    notificationId: string;
    onClose: () => void;
    onMessageSelect: (messageId: string) => void;
}

const COMMUNICATION_ENV = appConfig.apis.communicationApiEnv;
const API_KEY = COMMUNICATION_ENV.key;
const BASE_URL = COMMUNICATION_ENV.address;

const MessageList: React.FC<MessageListProps> = ({ notificationId, onClose, onMessageSelect }) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalMessages, setTotalMessages] = useState(0);

    useEffect(() => {
        loadMessages();
    }, [notificationId]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadMessages = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${BASE_URL}/notifications/${notificationId}/messages`, {
                headers: {
                    'x-api-key': API_KEY,
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setMessages(data.messages || []);
                setTotalMessages(data.totalMessages || 0);
            } else {
                setMessages([]);
                setTotalMessages(0);
                toast.error('Erro ao carregar mensagens');
            }
        } catch (error) {
            console.error('Erro ao carregar mensagens:', error);
            setMessages([]);
            setTotalMessages(0);
            toast.error('Erro ao carregar mensagens');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status: string) => {
        const colors = {
            'PENDING': 'bg-slate-500',
            'SENT': 'bg-green-600',
            'DELIVERED': 'bg-green-700',
            'ERROR': 'bg-red-600',
            'FAILED': 'bg-red-700',
        };
        return colors[status as keyof typeof colors] || 'bg-gray-500';
    };

    const getStatusLabel = (status: string) => {
        const labels: { [key: string]: string } = {
            'PENDING': 'Pendente',
            'SENT': 'Enviado',
            'DELIVERED': 'Entregue',
            'ERROR': 'Erro',
            'FAILED': 'Falhou',
        };
        return labels[status] || status;
    };

    const getRecipientIcon = (recipientType: string) => {
        return recipientType === 'WHATSAPP' ?
            <FaWhatsapp className="text-green-500" size={16} /> :
            <FaEnvelope className="text-blue-500" size={16} />;
    };

    return (
        <div className="h-full flex flex-col bg-white dark:bg-slate-800">
            {/* Header */}
            <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                        >
                            <FaArrowLeft size={16} />
                        </button>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Mensagens
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {totalMessages} mensagens encontradas
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
                {loading ? (
                    <div className="flex items-center justify-center h-32">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-32 text-gray-500 dark:text-gray-400">
                        <FaEnvelope size={32} className="mb-2 opacity-50" />
                        <p>Nenhuma mensagem encontrada</p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-200 dark:divide-slate-700">
                        {messages.map((message) => (
                            <div
                                key={message.messageId}
                                className="p-4 hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                                onClick={() => onMessageSelect(message.messageId)}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex items-start space-x-3 flex-1">
                                        {getRecipientIcon(message.recipientType)}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between">
                                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                    {message.recipient}
                                                </p>
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(message.status)}`}>
                                                    {getStatusLabel(message.status)}
                                                </span>
                                            </div>
                                            <div className="mt-1 flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                                                {message.sentAt && (
                                                    <div className="flex items-center space-x-1">
                                                        <FaCheck size={10} />
                                                        <span>Enviado: {message.sentAt}</span>
                                                    </div>
                                                )}
                                                {message.deliveredAt && (
                                                    <div className="flex items-center space-x-1">
                                                        <FaCheck size={10} className="text-green-500" />
                                                        <span>Entregue: {message.deliveredAt}</span>
                                                    </div>
                                                )}
                                                {message.status === 'ERROR' && (
                                                    <div className="flex items-center space-x-1 text-red-500">
                                                        <FaExclamationTriangle size={10} />
                                                        <span>Erro no envio</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export { MessageList as default };